import { useRef } from 'react';
import { corners, normAngle, R, sortBoxes, toManual } from '../../utils/card-cropper/crop.js';
import { Overlay, Stage } from './CardCropperPage.styles.js';

function pointsOf(geom) {
  return corners(geom).map((p) => p.join(',')).join(' ');
}

export default function ScanStage({
  scan,
  selectedBox,
  pad,
  numbers,
  draft,
  onSelect,
  onLive,
  onCommit,
  onCreate,
  onDraft,
}) {
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const W = scan.img.naturalWidth;
  const H = scan.img.naturalHeight;
  const fs = Math.max(14, W / 40);
  const hs = Math.max(W, H) / 90;

  const pointFromEvent = (e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return [
      ((e.clientX - rect.left) / rect.width) * W,
      ((e.clientY - rect.top) / rect.height) * H,
    ];
  };

  const onPointerDown = (e) => {
    const p = pointFromEvent(e);
    if (!p) return;
    const role = e.target?.dataset?.role || null;

    if (role === 'rot' && selectedBox) {
      toManual(selectedBox);
      dragRef.current = { type: 'rot', box: selectedBox };
    } else if (role === 'corner' && selectedBox) {
      toManual(selectedBox);
      const index = +e.target.dataset.i;
      dragRef.current = {
        type: 'corner',
        box: selectedBox,
        Q: corners(selectedBox)[(index + 2) % 4],
      };
    } else if (role === 'box') {
      const box = scan.boxes.find((item) => String(item.id) === e.target.dataset.id);
      if (!box) return;
      if (selectedBox !== box) onSelect(scan, box);
      const g = R(box);
      dragRef.current = {
        type: 'move',
        box,
        start: p,
        orig: [g.cx, g.cy],
        moved: false,
      };
    } else {
      if (selectedBox) onSelect(scan, null);
      dragRef.current = { type: 'draw', start: p };
    }

    const move = (ev) => {
      const drag = dragRef.current;
      if (!drag) return;
      const point = pointFromEvent(ev);
      if (!point) return;
      if (drag.type === 'draw') {
        const [x0, y0] = drag.start;
        onDraft({
          scanId: scan.id,
          x: Math.min(x0, point[0]),
          y: Math.min(y0, point[1]),
          width: Math.abs(point[0] - x0),
          height: Math.abs(point[1] - y0),
        });
        return;
      }
      const box = drag.box;
      if (drag.type === 'move') {
        const dx = point[0] - drag.start[0];
        const dy = point[1] - drag.start[1];
        if (!drag.moved && Math.hypot(dx, dy) < W / 200) return;
        if (!drag.moved) {
          toManual(box);
          drag.moved = true;
        }
        box.cx = drag.orig[0] + dx;
        box.cy = drag.orig[1] + dy;
      } else if (drag.type === 'rot') {
        box.angle = Math.atan2(point[1] - box.cy, point[0] - box.cx) + Math.PI / 2;
      } else if (drag.type === 'corner') {
        const c = Math.cos(box.angle);
        const s = Math.sin(box.angle);
        const Q = drag.Q;
        const dx = point[0] - Q[0];
        const dy = point[1] - Q[1];
        const du = dx * c + dy * s;
        const dv = -dx * s + dy * c;
        box.w = Math.max(10, Math.abs(du));
        box.h = Math.max(10, Math.abs(dv));
        const su = Math.sign(du) || 1;
        const sv = Math.sign(dv) || 1;
        const hu = su * box.w / 2;
        const hv = sv * box.h / 2;
        box.cx = Q[0] + hu * c - hv * s;
        box.cy = Q[1] + hu * s + hv * c;
      }
      onLive();
    };

    const end = (ev) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag) return;
      if (drag.type === 'draw') {
        onDraft(null);
        if (ev.type === 'pointercancel') return;
        const point = pointFromEvent(ev);
        if (!point) return;
        const w = Math.abs(point[0] - drag.start[0]);
        const h = Math.abs(point[1] - drag.start[1]);
        if (w > W * 0.04 && h > H * 0.04) {
          onCreate(scan, {
            cx: (drag.start[0] + point[0]) / 2,
            cy: (drag.start[1] + point[1]) / 2,
            w,
            h,
            angle: 0,
            on: true,
            manual: true,
            rot: null,
            cardLike: true,
          });
        }
        return;
      }
      if (drag.type === 'move' && !drag.moved) return;
      normAngle(drag.box);
      sortBoxes(scan);
      onCommit();
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    e.preventDefault();
  };

  const selectedHere = selectedBox && scan.boxes.includes(selectedBox) ? selectedBox : null;
  let knob = null;
  if (selectedHere) {
    const g = R(selectedHere);
    const c = Math.cos(g.angle);
    const s = Math.sin(g.angle);
    let off = g.h / 2 + hs * 3.5;
    let next = [g.cx + off * s, g.cy - off * c];
    if (next[0] < hs || next[1] < hs || next[0] > W - hs || next[1] > H - hs) {
      off = g.h / 2 - hs * 3.5;
      next = [g.cx + off * s, g.cy - off * c];
    }
    const top = [g.cx + (g.h / 2) * s, g.cy - (g.h / 2) * c];
    knob = { top, next, g };
  }

  return (
    <Stage>
      <img src={scan.url} alt="" draggable={false} />
      <Overlay
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        onPointerDown={onPointerDown}
      >
        {scan.boxes.map((box) => {
          const g = R(box);
          const margin = Math.min(g.w, g.h) * pad;
          const selected = selectedHere === box;
          const label = box.on ? numbers.get(box) : 'skip';
          return (
            <g key={box.id}>
              {box.on && Math.abs(margin) > 0.5 && (
                <polygon
                  className="margin"
                  points={pointsOf({ ...g, w: g.w + 2 * margin, h: g.h + 2 * margin })}
                />
              )}
              <polygon
                className={['box', !box.on && 'off', selected && 'sel'].filter(Boolean).join(' ')}
                points={pointsOf(g)}
                data-role="box"
                data-id={box.id}
              />
              <text
                x={g.cx}
                y={g.cy}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={fs}
                strokeWidth={Math.max(1, fs / 10)}
              >
                {label}
              </text>
            </g>
          );
        })}
        {knob && (
          <g>
            <line className="stem" x1={knob.top[0]} y1={knob.top[1]} x2={knob.next[0]} y2={knob.next[1]} />
            <circle
              className="handle rot"
              cx={knob.next[0]}
              cy={knob.next[1]}
              r={hs * 0.9}
              data-role="rot"
            />
            {corners(knob.g).map((p, i) => (
              <rect
                key={i}
                className="handle corner"
                x={p[0] - hs / 2}
                y={p[1] - hs / 2}
                width={hs}
                height={hs}
                data-role="corner"
                data-i={i}
                transform={`rotate(${(knob.g.angle * 180) / Math.PI} ${p[0]} ${p[1]})`}
              />
            ))}
          </g>
        )}
        {draft?.scanId === scan.id && (
          <rect className="draft" x={draft.x} y={draft.y} width={draft.width} height={draft.height} />
        )}
      </Overlay>
    </Stage>
  );
}
